import { useState, useContext, useMemo } from 'react';
import './ReportWriter.scss';
import EditableBox from './EditableBox';
import { Plus, Settings } from 'react-feather';
import { Button, Spinner } from '../../components/UIElements';
import SourcesStack from './SourcesStack';
import { SparkIcon } from '../../assets/icons/shared';
import { ReportsContext } from '../../context/reports';
import { TopicsContext } from '../../context/topics';
import { Popover } from 'react-tiny-popover';
import { useEntityContext, useFloatMenu } from '../../hooks';
import GeneratorSettingsMenu from './GeneratorSettingsMenu';
import { reportServices } from '../../services';
import { toast } from 'react-hot-toast';
import ConclusionInput from './ConclusionInput';
import Showdown from 'showdown';
import formatWrongMarkdownURL from '../../helpers/formatWrongMarkdownURL';
import RestrictedUI from '../../components/RestrictedUI';
import { PERMISSIONS } from '../../constants/memberPermissions';
import { ENTITIES } from '../../constants/entities';
import PermissionContext from '../../context/permissions/PermissionContext';
import { REPORT_AI_SUMMARY_TYPES } from '~/constants/ReportAISummaryTypes.enum';
import { TopicsProviderInterface } from '~/modules/Topics/types/TopicsContext.interface';
import { InsightInterfaceDetailed } from '~/interfaces/entities';
const converter = new Showdown.Converter();

const ReportWriter = ({
    intl,
    onSelectSource,
    getSourcesByTopic,
    lastUpdatedReport,
}) => {
    const {
        sections = [],
        loading,
        onCreateSection,
        onRemoveSection,
        onUpdateSection,
        selectedReport: report,
        setReports,
        reports,
    } = useContext(ReportsContext);

    const { topics, handlePinedTopic } =
        useEntityContext<TopicsProviderInterface>(TopicsContext);

    const { isAllowedTo } = useContext(PermissionContext);
    const [activeSection, setActiveSection] = useState(null);
    const [loadingConclusion, setLoadingConclusion] = useState(false);

    // AI generator settings
    const [AIsettings, setAISettings] = useState({
        summary_type: REPORT_AI_SUMMARY_TYPES.SHORT,
        include_report_section_sources: true,
        include_insight_links: true,
    });

    const conclusionMenu = useFloatMenu();

    const saveChanges = async (sectionId, text) => {
        if (!text) {
            await onRemoveSection(sectionId);
            setActiveSection(null);
            return;
        }

        const section = sections.find((s) => s.id === sectionId);

        const payload = {
            ...section,
            text,
        };

        // check, if the section text didn't change, don't update it
        const hasChanged = section.text !== text;

        if (hasChanged) await onUpdateSection(sectionId, payload);
        setActiveSection(null);
    };

    const handleCreateSection = async (sectionId, text) => {
        const payload = {
            report_id: report.id,
            text,
            position: sections.length,
        };
        if (text) await onCreateSection(payload);

        setActiveSection(null);
    };

    const handleSourceModal = (
        comments: InsightInterfaceDetailed[],
        section = {},
        isNew = false
    ) => {
        const topic = topics.find((t) => t.id === comments[0]?.topic_id);

        topic && handlePinedTopic(topic.id);

        const payload = {
            report,
            section,
            isNew,
            topic,
            commentIds: comments?.map((c) => c.id) || [],
        };

        onSelectSource(payload);
    };

    const generateConclusion = async () => {
        setLoadingConclusion(true);

        const [error, response] = await reportServices.generateConclusionWithAI(
            report.id,
            AIsettings
        );
        setLoadingConclusion(false);

        if (error) {
            toast.error(intl.formatMessage({ id: 'general_error' }));
            return;
        }

        const { data } = response;

        updateReportsArray(data);
    };

    const onSaveConclusion = async (text: string) => {
        const payload = {
            ...report,
            conclusion: text,
        };

        const [error, response] = await reportServices.updateReport(
            report.id,
            payload
        );

        if (error)
            return toast.error(intl.formatMessage({ id: 'general_error' }));

        const { data } = response;
        updateReportsArray(data);
    };

    const updateReportsArray = (newData) => {
        const repIdex = reports.findIndex((r) => r.id === report.id);
        const updatedReports = [...reports];
        updatedReports[repIdex] = newData;

        setReports(updatedReports);
    };

    const handleSettings = (name, value) => {
        setAISettings({
            ...AIsettings,
            [name]: value,
        });
    };

    const conclusionInputValue = useMemo(() => {
        const conc = converter
            .makeHtml(lastUpdatedReport.conclusion)
            .replace(/\s+(?=<)/g, ''); // remove blank spaces inside html tags

        // the converter library has a little bug that adds "<" and ">" symbols at the end and at the beginning of the markdown URL
        // https://github.com/showdownjs/showdown/issues/809
        // some existing conclusions were stored with those invalid characters, remove them
        const parsed = formatWrongMarkdownURL(conc);

        return parsed;
    }, [lastUpdatedReport.conclusion]);

    const canEdit = useMemo(() => {
        return isAllowedTo(ENTITIES.REPORTS, PERMISSIONS.EDIT);
    }, [isAllowedTo]);

    return (
        <div className="cg-report-writer">
            {sections.map((section) => (
                <div
                    key={section.id}
                    className="cg-report-writer__input-container"
                >
                    <EditableBox
                        value={section.text}
                        sectionId={section.id}
                        onSave={saveChanges}
                        isActive={activeSection === section.id}
                        onFocus={() => setActiveSection(section.id)}
                        disabled={loading || loadingConclusion || !canEdit}
                    />
                    <SourcesStack
                        intl={intl}
                        sources={getSourcesByTopic(section)}
                        onSelectSource={(src, isNew) =>
                            handleSourceModal(src, section, isNew)
                        }
                    />
                </div>
            ))}

            {activeSection === 'new' && (
                <EditableBox
                    value={''}
                    sectionId={crypto.randomUUID()}
                    onSave={handleCreateSection}
                    isActive={true}
                    disabled={loading}
                    onFocus={() => {}}
                />
            )}

            <div className="cg-report-writer__footer">
                {loading ? (
                    <Spinner width="20" height="20" weight="2" />
                ) : (
                    <RestrictedUI
                        to={PERMISSIONS.EDIT}
                        entity={ENTITIES.REPORTS}
                    >
                        <Button
                            iconBefore={Plus}
                            variant="tertiary"
                            onClick={(e) => {
                                setActiveSection('new');
                                e.stopPropagation();
                            }}
                        >
                            {intl.formatMessage({ id: 'add_an_idea' })}
                        </Button>
                    </RestrictedUI>
                )}
            </div>

            <div className="cg-report-writer__conclusion">
                <div className="cg-report-writer__conclusion-header">
                    <h4> {intl.formatMessage({ id: 'conclusion' })} </h4>
                    {sections.length > 0 && (
                        <RestrictedUI
                            to={PERMISSIONS.EDIT}
                            entity={ENTITIES.REPORTS}
                        >
                            <div>
                                <Button
                                    iconBefore={SparkIcon}
                                    variant="secondary"
                                    onClick={generateConclusion}
                                    loading={loadingConclusion}
                                    disabled={loadingConclusion || loading}
                                >
                                    {intl.formatMessage({
                                        id: report.conclusion
                                            ? 'rewrite_with_ai'
                                            : 'write_with_ai',
                                    })}
                                </Button>

                                <Popover
                                    isOpen={conclusionMenu.showMenu}
                                    align={'end'}
                                    positions={['bottom', 'top']} // preferred positions by priority
                                    content={
                                        <GeneratorSettingsMenu
                                            settings={AIsettings}
                                            onChange={handleSettings}
                                            intl={intl}
                                        />
                                    }
                                    onClickOutside={() =>
                                        conclusionMenu.handleMenu()
                                    }
                                >
                                    <Button
                                        onClick={(e) => {
                                            conclusionMenu.handleMenu();
                                            e.stopPropagation();
                                        }}
                                        iconBefore={Settings}
                                        variant="darken"
                                        disabled={loadingConclusion}
                                    >
                                        {intl.formatMessage({
                                            id: 'optimize_ai',
                                        })}
                                    </Button>
                                </Popover>
                            </div>
                        </RestrictedUI>
                    )}
                </div>
                {!loading && (
                    <ConclusionInput
                        value={conclusionInputValue}
                        disabled={loadingConclusion || loading || !canEdit}
                        onSave={onSaveConclusion}
                        placeholder={
                            intl.formatMessage({ id: 'write_summary_ideas' }) +
                            '...'
                        }
                        loading={loading}
                    />
                )}
            </div>
        </div>
    );
};

export default ReportWriter;
